/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
// import { NextSeo } from 'next-seo';
import { NextRouter, withRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { BASE_URL, EASING_VALUE, SEO_INFO } from 'libs/constants';
import { Layout } from 'components/templates/layout';
import { Container } from 'components/organisms/container';
import { Row } from 'components/organisms/row';
import { Col } from 'components/organisms/col';
import { Heading } from 'components/atoms/heading';
import { Text } from 'components/atoms/text';
import { BackButton } from 'components/atoms/back-button';
import { motion } from 'framer-motion';
import { InView } from 'react-intersection-observer';

interface PrivacyPolicyPageProps {
  router: NextRouter;
  lang?: string;
}

/* const preloadList = [
  { rel: 'preload', href: '/images/home/case-study.jpg', type: 'image/jpg', as: 'image', media: '(max-width: 768px)' },
  {
    rel: 'preload',
    href: '/images/home/case-study@2x.jpg',
    type: 'image/jpg',
    as: 'image',
    media: '(-webkit-min-device-pixel-ratio: 2), (max-width: 768px)',
  },
  { rel: 'preload', href: '/images/home/case-study.jpg', type: 'image/jpg', as: 'image', media: '(min-width: 769px)' },
  {
    rel: 'preload',
    href: '/images/home/case-study@2x.jpg',
    type: 'image/jpg',
    as: 'image',
    media: '(-webkit-min-device-pixel-ratio: 2), (min-width: 769px)',
  },
]; */

const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = () => {
  const PAGE_KEY = 'PRIVACY_POLICY';
  const PAGE_SEO_INFO = (SEO_INFO as any)[PAGE_KEY] as any;
  const PAGE_URL = `${BASE_URL}/privacy-policy`;

  const detailList = [
    {
      title: `Collection and Use of Personal Information`,
      description: `Personal information shall be collected only after informing the customers of the purpose and obtaining consent for doing so, except when the rights or interests of customers or third parties may be injured. The scope of use of the personal information collected is limited, and, the information shall be handled appropriately within this scope.`,
    },
    {
      title: `Provision of Personal Information`,
      description: `Personal information collected by us shall not be provided to third parties without prior express consent of the customers, except as required by law or regulation.`,
    },
    {
      title: `Entrustment of Personal Information`,
      description: `We may entrust customers’ personal information with authorized third parties. In all such cases, we shall select the recipient with great care from among those parties that have established a sufficient level of protection of the personal information, and make necessary supervision or otherwise ensure, through a contract, etc. stipulating the level of protection to be observed, that the recipient manages the information properly.`,
    },
    {
      title: `Use of Cookies`,
      description: `To make our websites work properly, we place small data files called “cookies” on your device. A cookie is typically a small piece of data sent to your computer device from a website and stored in your web browser while you are browsing a website.<br/>
      <br/>
      Most web browsers automatically accept cookies and similar technologies, but if you prefer, you can change your browser to prevent that. However, you may not be able to take full advantage of our websites if you do so.`,
    },
    {
      title: `Type of Cookie used on Our Site`,
      description: `Analytical Cookies:<br/>
      These Cookies are used by us or third-party service providers to analyze how our Sites are used and how they are performing. For example, we use cookies to know which country you are in when you request a service to help base the contents on your location. We also use some Cookies from Google Analytics to obtain web analytics and intelligence about our Site. These tools use programming code to collect information about your interaction with our Site, such as the pages you visit, the links you click on and how long you are on our Site. These tools do not store any personal identifiable information.`,
    },
  ];

  const variants = {
    root: {
      enter: {
        transition: {
          staggerChildren: 0.5,
        },
      },
      exit: {
        transition: { staggerChildren: 0.05, staggerDirection: -1 },
      },
    },
    parent: {
      enter: {
        transition: {
          staggerChildren: 0.25,
        },
      },
      exit: {
        transition: { staggerChildren: 0.05, staggerDirection: -1 },
      },
    },
    text: {
      enter: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 1.5,
          ease: EASING_VALUE,
        },
      },
      exit: {
        opacity: 0,
        y: 10,
      },
    },
    list: {
      enter: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 1.5,
          ease: EASING_VALUE,
          staggerChildren: 0.25,
        },
      },
      exit: {
        opacity: 0,
        y: 10,
        transition: {
          staggerChildren: 0.05,
          staggerDirection: -1,
        },
      },
    },
  };

  return (
    <Layout pageId="/privacy-policy">
      <NextSeo
        title={PAGE_SEO_INFO.title}
        description={PAGE_SEO_INFO.description}
        openGraph={{
          type: 'website',
          url: PAGE_URL,
          title: PAGE_SEO_INFO.title,
          description: PAGE_SEO_INFO.description,
          site_name: SEO_INFO.HOME.title,
        }}
        // additionalLinkTags={preloadList}
      />
      <div className="p-privacy-policy p-legal">
        <div className="p-legal__graphic">
          <svg width="884.863" height="701.113" viewBox="0 0 884.863 701.113">
            <g id="Group_25936" data-name="Group 25936" transform="translate(154.215 -13.077) rotate(14)">
              <path
                id="Path_22315"
                data-name="Path 22315"
                d="M63.442,50.967,76.16,0S36.891,16.024,0,34.138Z"
                transform="translate(681.888 75.161)"
                fill="#001e42"
              />
              <path
                id="Path_22316"
                data-name="Path 22316"
                d="M93.417,0S66.9,46.892,53.466,73.568L0,72Z"
                transform="translate(705.805 185.322)"
                fill="#fca518"
              />
              <path
                id="Path_22317"
                data-name="Path 22317"
                d="M113.183,47.543,88.9,0,0,80.765Z"
                transform="translate(585.125 95.441)"
                fill="#ffe900"
              />
              <path
                id="Path_22319"
                data-name="Path 22319"
                d="M45.936,0,0,82.044S47.639,30.7,72.262,11.139Z"
                transform="translate(629.312 259.068)"
                fill="#df1f1c"
              />
              <path
                id="Path_22320"
                data-name="Path 22320"
                d="M174.56,52.807,200.284,0S81.168,49.67,0,95.627Z"
                transform="translate(433.564 67.051)"
                fill="#001976"
              />
              <path
                id="Path_22321"
                data-name="Path 22321"
                d="M139.56,0,0,46.316,105.663,60.809Z"
                transform="translate(497.05 214.191)"
                fill="#0092e5"
              />
              <path
                id="Path_22322"
                data-name="Path 22322"
                d="M186.638,0,134.486,92.775,0,48.345Z"
                transform="translate(435.144 139.541) rotate(42)"
                fill="#8ea7cd"
              />
              <path
                id="Path_22323"
                data-name="Path 22323"
                d="M80.963,0A854.672,854.672,0,0,0,0,62.805L186.635,14.493Z"
                transform="translate(262.759 97.146)"
                fill="#cbdae9"
                opacity="0.6"
              />
              <path
                id="Path_22324"
                data-name="Path 22324"
                d="M80.064,0,0,66.149,214.576,44.49Z"
                transform="translate(207.499 154.386)"
                fill="#fca518"
              />
              <path
                id="Path_22325"
                data-name="Path 22325"
                d="M160.913,0S116,53.944,102.692,74.676L0,16.263Z"
                transform="translate(354.216 449.044)"
                fill="#ffe900"
              />
              <path
                id="Path_22326"
                data-name="Path 22326"
                d="M110.823,59.761,67.924,0,0,131.281S60.229,87.954,110.823,59.761Z"
                transform="translate(365.927 485.906)"
                fill="#0092e5"
              />
              <path
                id="Path_22327"
                data-name="Path 22327"
                d="M66.237,0,0,60.514l60.253,87.177Z"
                transform="translate(195.739 343.668)"
                fill="#fca518"
              />
              <path
                id="Path_22328"
                data-name="Path 22328"
                d="M123.108,34.423,64.114,0S19.141,41.106,0,60.836l69.389,23.3Z"
                transform="translate(184.824 266.356)"
                fill="#cbdae9"
                opacity="0.6"
              />
              <path
                id="Path_22329"
                data-name="Path 22329"
                d="M149.432,0,0,43.17l161.495,62.116Z"
                transform="translate(140.77 507.216)"
                fill="#cbdae9"
                opacity="0.6"
              />
              <path
                id="Path_22330"
                data-name="Path 22330"
                d="M112.633,49.9,67.067,0,0,80.008Z"
                transform="translate(-49.169 360.245)"
                fill="#ffe900"
              />
              <path
                id="Path_22331"
                data-name="Path 22331"
                d="M0,0,45.548,127.472s73.9-94.6,96.776-116.842Z"
                transform="translate(78.458 75.501)"
                fill="#8ea7cd"
              />
              <path
                id="Path_22332"
                data-name="Path 22332"
                d="M7.231,0,0,80.358l91.768,60.3Z"
                transform="translate(119.428 529.553)"
                fill="#0092e5"
              />
              <path
                id="Path_22333"
                data-name="Path 22333"
                d="M151.815,0,0,32.151,85.574,60.507Z"
                transform="translate(29.202 254.307)"
                fill="#df1f1c"
              />
              <ellipse
                id="Ellipse_3085"
                data-name="Ellipse 3085"
                cx="60.521"
                cy="60.521"
                rx="60.521"
                ry="60.521"
                transform="translate(353.715 191.801)"
                fill="#df1f1c"
              />
              <ellipse
                id="Ellipse_3086"
                data-name="Ellipse 3086"
                cx="83.262"
                cy="83.263"
                rx="83.262"
                ry="83.263"
                transform="translate(-35.557 22.343)"
                fill="#df1f1c"
              />
              <ellipse
                id="Ellipse_3088"
                data-name="Ellipse 3088"
                cx="61.622"
                cy="61.622"
                rx="61.622"
                ry="61.622"
                transform="translate(254.343 359.541)"
                fill="#001976"
              />
            </g>
          </svg>
        </div>
        <InView triggerOnce>
          {({ inView, ref }) => (
            <motion.div
              className="p-legal__wrapper"
              initial={false}
              animate={inView ? 'enter' : 'exit'}
              variants={variants.root}
            >
              <motion.section ref={ref} className="p-legal__head" variants={variants.parent}>
                <Container>
                  <Row>
                    <Col width={2} widthSP={4}></Col>
                    <Col width={8} widthSP={4}>
                      <motion.div className="p-legal__head-title" variants={variants.text}>
                        <Heading modifiers="dot" size="large">
                          Privacy Policy
                        </Heading>
                      </motion.div>
                      <motion.div className="p-legal__head-description" variants={variants.text}>
                        <Text>
                          v-PaSSer (“we”, “our”, or “us”) recognizes the importance of personal information and ensures
                          the protection and safeguarding of its customers’ personal information as a fundamental
                          principle of its business and its responsibility to society.
                        </Text>
                      </motion.div>
                    </Col>
                    <Col width={2} widthSP={4}></Col>
                  </Row>
                </Container>
              </motion.section>
              <section className="p-legal__detail-list">
                <motion.div className="p-legal__detail-list-top" variants={variants.list}>
                  <Container>
                    <Row>
                      <Col width={2} widthSP={4}></Col>
                      <Col width={8} widthSP={4}>
                        {detailList
                          .filter((_item, index) => index < 2)
                          .map((item, index) => (
                            <motion.div
                              key={index}
                              className="p-legal__detail-item p-legal__detail-item--top"
                              variants={variants.parent}
                            >
                              <motion.div className="p-legal__detail-item-title" variants={variants.text}>
                                <Text modifiers="body-1">{item.title}</Text>
                              </motion.div>
                              <motion.div className="p-legal__detail-item-description" variants={variants.text}>
                                <p dangerouslySetInnerHTML={{ __html: item.description }} />
                              </motion.div>
                            </motion.div>
                          ))}
                      </Col>
                      <Col width={2} widthSP={4}></Col>
                    </Row>
                  </Container>
                </motion.div>
                <Container>
                  <Row>
                    <Col width={2} widthSP={4}></Col>
                    <Col width={8} widthSP={4}>
                      {detailList
                        .filter((_item, index) => index >= 2)
                        .map((item, index) => (
                          <InView key={index} triggerOnce>
                            {({ inView, ref }) => (
                              <motion.div
                                ref={ref}
                                className="p-legal__detail-item"
                                initial={false}
                                animate={inView ? 'enter' : 'exit'}
                                variants={variants.parent}
                              >
                                <motion.div className="p-legal__detail-item-title" variants={variants.text}>
                                  <Text modifiers="body-1">{item.title}</Text>
                                </motion.div>
                                <motion.div className="p-legal__detail-item-description" variants={variants.text}>
                                  <p dangerouslySetInnerHTML={{ __html: item.description }} />
                                </motion.div>
                              </motion.div>
                            )}
                          </InView>
                        ))}
                    </Col>
                    <Col width={2} widthSP={4}></Col>
                  </Row>
                </Container>
              </section>
            </motion.div>
          )}
        </InView>
        <section className="p-legal__back">
          <BackButton path="/" />
        </section>
      </div>
    </Layout>
  );
};

export default withRouter(PrivacyPolicyPage);
